import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { MemberDetailContent } from '../../components/features/MemberDetailContent.js';
import { OneOnOneTimelineCard } from '../../components/features/one-on-one/OneOnOneTimelineCard.js';
import { MemberDleEvaluationTab } from '../../components/features/dle-mypage/MemberDleEvaluationTab.js';
import { useDleScoresByEmail } from '../../hooks/useDleEvaluationSurvey.js';
import { ProtectedRoute } from '../../components/auth/index.js';

/**
 * メンバー詳細のタブ。KPI（従来のメンバー詳細）/ 1on1（週次タイムライン）/
 * DLE評価（そのメンバーが DLE として受けた評価）。
 * @graph-connects none
 */
type MemberDetailTabId = 'kpi' | 'oneOnOne' | 'dleEval';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business メンバー詳細ページ。KPI タブ（共通MemberDetailContent）と 1on1 タブ（開設・週次タイムライン）を戻るボタン付きで表示
 * @graph-connects MemberDetailContent [calls] メンバー詳細共通コンポーネント
 * @graph-connects OneOnOneTimelineCard [calls] 1on1 の開設と週次タイムライン
 * @graph-connects MemberDleEvaluationTab [calls] そのメンバーの DLE 評価
 */
export const Route = createFileRoute('/members/$email')({
  component: MemberDetailPage,
});

/** @graph-connects ProtectedRoute [calls] VIEW_MEMBERS 権限でアクセスをガードする */
function MemberDetailPage() {
  return (
    <ProtectedRoute pageKey="members">
      <MemberDetailContentPage />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function MemberDetailContentPage() {
  const { t } = useTranslation();
  const { email } = Route.useParams();
  const decodedEmail = decodeURIComponent(email);
  const [activeTab, setActiveTab] = useState<MemberDetailTabId>('kpi');
  // DLE評価タブは「相手が DLE」かつ「自分に閲覧権限がある」ときだけ出す。
  // 権限が無ければ API が 403 を返して data が undefined のままになり、タブは出ない。
  // 相手が DLE でなければ isDle=false が返る（こちらもタブを出さない）。
  const { data: dleScores } = useDleScoresByEmail(decodedEmail);
  const showDleEval = dleScores?.isDle ?? false;
  const tabs: { id: MemberDetailTabId; label: string }[] = [
    { id: 'kpi', label: 'KPI' },
    { id: 'oneOnOne', label: t('oneOnOne.tab') },
    ...(showDleEval ? [{ id: 'dleEval' as const, label: t('dleEvaluation.tab') }] : []),
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* 戻るボタン + タイトル */}
      <div className="flex items-center gap-4">
        <Link
          to="/members/engineer-board"
          className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
          aria-label={t('actions.back')}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </Link>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('members.detail.title')}
        </h1>
      </div>

      {/* タブ（マイページと同じ見た目） */}
      <div className="flex border-b border-[var(--color-border)]" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => {
              setActiveTab(tab.id);
            }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-[var(--color-accent-primary)] border-b-2 border-[var(--color-accent-primary)]'
                : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* タブコンテンツ */}
      <div role="tabpanel">
        {activeTab === 'kpi' && <MemberDetailContent email={decodedEmail} />}
        {activeTab === 'oneOnOne' && <OneOnOneTimelineCard email={decodedEmail} showPlaceholder />}
        {activeTab === 'dleEval' && showDleEval && <MemberDleEvaluationTab email={decodedEmail} />}
      </div>
    </div>
  );
}
